import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  getCoinbaseTransactions,
  CoinbaseTransactionItem,
  CoinbaseTransactionsParams,
} from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatTimestamp, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';

// =============================================================================
// SERVER-SIDE PAGINATION IDIOM (canonical reference for the tax-reporting views)
// =============================================================================
// Sibling tax views (SystemTrades, Reconciliation, TaxLots, CostBasis,
// MissingCostBasis) reuse the same shape — change here, mirror there.
//
// Rules:
//   1. Component owns a `pageIndex` + `pageSize` in useState (defaults 0, 100).
//   2. The TanStack Query key includes pageIndex/pageSize and every filter and
//      the auto-refresh trigger, so a change in any of them refetches.
//   3. The query function passes `page = pageIndex + 1` to the API (1-based per
//      CLAUDE.md API standards).
//   4. Render via the shared `<DataTable serverPagination={...} />` component.
//      It owns the pagination chrome, filter chrome (`filters` prop), and
//      sortable headers — the view component owns the params + state only.
//   5. Filter changes reset pageIndex to 0 (otherwise users land on a page
//      that doesn't exist for the new filter set).
//
// See PLAN-030 / ADR-6 in spec-driven/tax_reporting_ui_foundation/plan.md.
// =============================================================================

export const CoinbaseTransactionsView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [portfolioId, setPortfolioId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_time', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: CoinbaseTransactionsParams = {
    taxYear,
    portfolioId: portfolioId || undefined,
    productId: productId || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'coinbase-transactions', taxYear, portfolioId, productId, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getCoinbaseTransactions(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();
  const productOptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.product_id))).sort().map((p) => ({ value: p, label: p })),
    [items]
  );

  const columns = useMemo<ColumnDef<CoinbaseTransactionItem>[]>(
    () => [
      {
        accessorKey: 'created_time',
        header: 'Created',
        cell: (info) => <span className="date-cell">{formatTimestamp(info.getValue() as string)}</span>,
      },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      {
        accessorKey: 'trade_side',
        header: 'Side',
        cell: (info) => (
          <span className={`badge badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'order_status',
        header: 'Status',
        cell: (info) => (
          <span className={`badge badge-status badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
      },
      { accessorKey: 'filled_size', header: 'Filled Size', cell: (info) => formatCrypto(info.getValue() as string) },
      {
        accessorKey: 'average_filled_price',
        header: 'Avg Price',
        cell: (info) => <span className="price-cell">{formatUsd(info.getValue() as string | null)}</span>,
      },
      { accessorKey: 'filled_value', header: 'Filled Value', cell: (info) => formatUsd(info.getValue() as string) },
      {
        accessorKey: 'total_fees',
        header: 'Fees',
        cell: (info) => <span className="fee-cell">{formatUsd(info.getValue() as string)}</span>,
      },
      { accessorKey: 'total_value_after_fees', header: 'Net', cell: (info) => formatUsd(info.getValue() as string) },
      {
        accessorKey: 'coinbase_order_id',
        header: 'Order ID',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    {
      id: 'portfolio_id',
      label: 'Portfolio',
      value: portfolioId,
      options: portfolioOptions,
      onChange: onFilterChange(setPortfolioId),
    },
    {
      id: 'product_id',
      label: 'Product',
      value: productId,
      options: productOptions,
      onChange: onFilterChange(setProductId),
    },
  ];

  return (
    <div aria-label="Coinbase Transactions view">
      <DataTable<CoinbaseTransactionItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading transactions…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load transactions'}
        emptyMessage="No Coinbase transactions for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default CoinbaseTransactionsView;
